import { useMemo } from 'react';
import { BookOpenText, Sparkles } from 'lucide-react';
import type { Glossary, GlossaryTerm, Key } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  k: Key;
  lang: string;
  glossary: Glossary | undefined;
  /** Source value the AI proposed (post-translation), if any */
  aiSuggestion?: string;
  aiMeta?: { provider: string; model: string; latencyMs?: number; costClass?: string };
  onApplySuggestion?: (value: string) => void;
}

/**
 * Bottom ribbon below the editor showing glossary chips that apply to the
 * current source string and (when available) an AI suggestion produced by the
 * most recent translate run.
 */
export function SuggestionRibbon({
  k,
  lang,
  glossary,
  aiSuggestion,
  aiMeta,
  onApplySuggestion,
}: Props) {
  const hits = useMemo(() => matchTerms(glossary?.terms ?? [], k.sourceText), [glossary, k.sourceText]);

  if (hits.length === 0 && !aiSuggestion) return null;

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      {aiSuggestion && (
        <div className="flex items-start gap-2">
          <span className="mt-0.5 grid size-5 place-items-center rounded-full bg-primary/15 text-primary">
            <Sparkles className="size-3" aria-hidden />
          </span>
          <div className="flex-1 space-y-1">
            <p className="text-xs font-medium text-foreground">AI suggestion</p>
            <p className="text-sm">{aiSuggestion}</p>
            {aiMeta && (
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {aiMeta.provider} · {aiMeta.model}
                {aiMeta.latencyMs !== undefined ? ` · ${aiMeta.latencyMs} ms` : ''}
                {aiMeta.costClass ? ` · ${aiMeta.costClass}` : ''}
              </p>
            )}
          </div>
          {onApplySuggestion && (
            <button
              type="button"
              onClick={() => onApplySuggestion(aiSuggestion)}
              className="rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/20"
            >
              Apply
            </button>
          )}
        </div>
      )}
      {hits.length > 0 && (
        <div className="flex flex-wrap items-start gap-2">
          <span className="mt-0.5 grid size-5 place-items-center rounded-full bg-positive/15 text-positive">
            <BookOpenText className="size-3" aria-hidden />
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Glossary
          </span>
          <ul className="flex flex-wrap items-center gap-1.5">
            {hits.map((hit) => {
              const target = hit.translations?.[lang];
              return (
                <li
                  key={hit.source}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border border-border bg-surface px-1.5 py-0.5 text-[11px]',
                    hit.doNotTranslate && 'border-warning/40 text-warning',
                  )}
                  title={hit.description ?? undefined}
                >
                  <span className="font-medium">{hit.source}</span>
                  {hit.doNotTranslate ? (
                    <span className="text-[10px] uppercase">do not translate</span>
                  ) : target ? (
                    <>
                      <span aria-hidden>→</span>
                      <span>{target}</span>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function matchTerms(terms: GlossaryTerm[], source: string): GlossaryTerm[] {
  if (!source) return [];
  const lower = source.toLowerCase();
  return terms.filter((t) => {
    if (!t.source) return false;
    if (t.caseSensitive) return source.includes(t.source);
    return lower.includes(t.source.toLowerCase());
  });
}
